import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { HotTable } from '@handsontable/react'
import { textRenderer } from 'handsontable/renderers'

import { isNumeric, withThousands } from '../helpers'
import { useSelectCells } from '../hooks'
import { createTableContext, getMergeCells, mergeData } from '../setup'
import styles from './Table.module.css'

type Props = {
  data: (string | number)[][]
  sendSelections?: (values: [number[], number[]]) => void
  colHeaders: { name: string; span: number }[][]
  rowHeaders: string[]
  tableMeta?: object[][]
  disabledRows?: number[]
  rowHeaderWidth?: number
  width?: string
  height?: string
  selectionMode?: 'multiple' | 'range' | 'single'
  selectionStrategy?: 'column' | 'row' | 'freeform' | 'group'
  autoRowSize?: boolean
  modern?: boolean
}

function colHeaderRenderer(instance, td, row, col, prop, value, cellProperties) {
  textRenderer.apply(this, arguments)
  td.style.background = '#f3f3f3'
}

function rowHeaderRenderer(instance, td, row, col, prop, value, cellProperties) {
  textRenderer.apply(this, arguments)
  td.style.background = '#f3f3f3'
}

function disabledRenderer(instance, td, row, col, prop, value, cellProperties) {
  textRenderer.apply(this, arguments)
  td.style.opacity = 0.4
  td.style.color = '#a1a1a1'
  td.style.background = '#f9f9f9'
}

function modernValueRenderer(instance, td, row, col, prop, value, cellProperties) {
  textRenderer.call(this, instance, td, row, col, prop, withThousands(value), cellProperties)
  td.classList.toggle('is-number', isNumeric(value))
}

const Table = (props: Props) => {
  const {
    sendSelections,
    data = [],
    colHeaders = [],
    rowHeaders = [],
    tableMeta = [],
    disabledRows = [],
    width = '100%',
    height = '100%',
    autoRowSize = true,
    selectionMode = 'multiple',
    selectionStrategy = 'freeform',
    modern = false
  } = props
  const tableRef = useRef(null)
  const context = useMemo(() => {
    return createTableContext(data, rowHeaders, colHeaders, tableMeta)
  }, [data, rowHeaders, colHeaders, tableMeta])
  const mergedCells = useMemo(() => {
    return getMergeCells(colHeaders, context)
  }, [colHeaders, context])

  const [afterSelectionEnd, onDeselect] = useSelectCells(
    tableRef?.current?.hotInstance,
    selectionStrategy,
    context
  )
  const [highlight, setHighlight] = useState<[number[], number[]]>([[], []])
  const afterSelection = (...args) => {
    //@ts-ignore
    let [rows, columns] = afterSelectionEnd(...args)
    rows = rows.filter(idx => !disabledRows.includes(idx))
    sendSelections([rows, columns])
    if (modern) {
      setHighlight([rows, columns])
    }
  }

  useEffect(() => setHighlight([[], []]), [data, selectionStrategy])

  const updateData = useCallback(() => {
    const mergedData = mergeData(data, rowHeaders, colHeaders, tableMeta, context)
    try {
      tableRef.current.hotInstance.updateSettings({ data: mergedData, mergeCells: mergedCells })
    } catch (e) {
      console.log(e)
    }
  }, [data, rowHeaders, colHeaders, tableMeta, context, mergedCells])

  useEffect(() => updateData(), [updateData])

  const renderModernCells = (row, col) => {
    const dataRow = row - context.data[0][0]
    const dataCol = col - context.data[0][1]
    const [rowSet, colSet] = highlight
    const classes = []
    let renderer = textRenderer
    if (row <= context.meta[1][0]) {
      classes.push('cell-meta')
    } else if (row >= context.header.col[0][0] && row <= context.header.col[1][0]) {
      const isLastLevel = row === context.header.col[1][0]
      classes.push(isLastLevel ? 'cell-col-header' : 'cell-group-header')
      if (isLastLevel && col !== 0 && selectionStrategy !== 'row' && colSet.includes(dataCol)) {
        classes.push('is-selected-header')
      }
    } else if (col === 0) {
      classes.push('cell-row-header')
      if (selectionStrategy === 'row' && rowSet.includes(dataRow)) {
        classes.push('is-selected-header')
      }
    } else {
      classes.push('cell-value')
      renderer = modernValueRenderer
      if (rowSet.includes(dataRow) && colSet.includes(dataCol)) {
        classes.push('is-selected')
      }
    }
    if (dataRow >= 0 && disabledRows.includes(dataRow)) {
      classes.push('is-disabled')
    }
    return { renderer, className: classes.join(' ') }
  }

  const renderCells = (row, col, ...rest) => {
    if (modern) {
      return renderModernCells(row, col)
    }
    if (row >= context.header.col[0][0] && row <= context.header.col[1][0] && col !== 0) {
      return { renderer: colHeaderRenderer }
    }
    if (row >= context.header.row[0][0] && row <= context.header.row[1][0] && col === 0) {
      return { renderer: rowHeaderRenderer }
    }
    if (disabledRows.includes(row - context.data[0][0])) {
      return { renderer: disabledRenderer }
    }
  }

  return (
    <HotTable
      readOnly={true}
      readOnlyCellClassName={modern ? 'is-readonly' : styles.readonly}
      className={modern ? 'modern-grid' : undefined}
      ref={tableRef}
      width={width}
      height={height}
      autoRowSize={autoRowSize}
      licenseKey="non-commercial-and-evaluation"
      cells={renderCells}
      selectionMode={selectionMode}
      afterSelectionEnd={afterSelection}
      afterDeselect={onDeselect}
    />
  )
}

export default Table
